import type { ICreateAwardCategory } from '../../../redux/contest/types';

function formatCentsAsUsd(cents: number): string {
    return `$${(cents / 100).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    })}`;
}

/** Wizard/UI prize amounts are USD dollars; AwardCategory.prize is stored in cents (per funding API). */
export function prizeDollarsToApiCents(dollars: number): number {
    return Math.round((Number(dollars) || 0) * 100);
}

export type AwardPrizeDisplaySource = {
    prize?: number | string | null;
    formatted_for_display?: string | null;
    defaulted_to_usd?: boolean;
};

export function getAwardPrizeCents(prize: number | string | null | undefined): number {
    const cents = Math.round(Number(prize) || 0);
    return cents > 0 ? cents : 0;
}

/** Display AwardCategory.prize (cents) as USD — matches funding API / escrow totals. */
export function formatAwardPrizeForDisplay(award: AwardPrizeDisplaySource): string {
    // Only trust localized display when the API explicitly defaulted currency.
    // Otherwise prefer prize cents so edits (PATCH) are not masked by a stale string.
    if (award.defaulted_to_usd && award.formatted_for_display?.trim()) {
        const s = award.formatted_for_display.trim();
        return s.startsWith('$') ? s : `$${s}`;
    }
    const cents = getAwardPrizeCents(award.prize);
    if (cents > 0) return formatCentsAsUsd(cents);
    if (award.formatted_for_display?.trim()) {
        const s = award.formatted_for_display.trim();
        return s.startsWith('$') || /^[₦€£]/.test(s) ? s : `$${s}`;
    }
    return '';
}

/** Edit forms collect dollars; API stores cents. */
export function awardPrizeCentsToFormDollars(prize: number | string | null | undefined): string {
    const cents = getAwardPrizeCents(prize);
    if (cents <= 0) return '';
    const dollars = cents / 100;
    return Number.isInteger(dollars) ? String(dollars) : dollars.toFixed(2).replace(/\.?0+$/, '');
}

export function awardPrizeFormDollarsToApiValue(dollars: number | string): number {
    return prizeDollarsToApiCents(Number(dollars) || 0);
}

export function sumAwardPrizeCents(awards: AwardPrizeDisplaySource[]): number {
    return awards.reduce((sum, award) => sum + getAwardPrizeCents(award.prize), 0);
}

export type WizardAwardCategoryPayload = ICreateAwardCategory & {
    /** Placement in prize_structure (e.g. "1st Place") — required for tiered awards in manage flow */
    position?: string;
    tier_label?: string;
    tier_position?: string;
};

/**
 * Builds POST /awardcategory body matching CreateAwardCategoryDialog + funding API.
 * Funding totals come from AwardCategory.prize (integer cents on the server).
 */
export function buildAwardCategoryCreatePayload(input: {
    contestId: string;
    submissionCategoryId: string;
    placementPosition: string;
    tierLabel: string;
    tierName: string;
    prizeDollars: number;
    additionalPrizes: string[];
}): WizardAwardCategoryPayload {
    const placement = input.placementPosition.trim();
    const tierLabel = input.tierLabel.trim();

    return {
        contest: input.contestId,
        category: input.submissionCategoryId,
        category_title: placement,
        prize: String(prizeDollarsToApiCents(input.prizeDollars)),
        additional_prize: input.additionalPrizes,
        position: placement,
        tier_label: tierLabel,
        tier_name: input.tierName,
        tier_position: placement,
    };
}
